import { DOCUMENT } from '@angular/common';
import { Service, inject, signal } from '@angular/core';

/**
 * Whether the running page is older than what the server now serves.
 *
 * Every build hashes its chunk names and a deploy replaces the whole set, so a
 * tab opened before the deploy asks for chunks that no longer exist the moment
 * it navigates somewhere it has not been yet. Static Web Apps answers those
 * requests with `index.html`, and the browser reports a failed dynamic import.
 *
 * A failed import alone proves nothing, though — a dropped connection or the
 * dev server re-bundling a dependency fails the same way. So the failure is
 * only the trigger: the service then fetches the current `index.html` and
 * compares the main script it names with the one this page runs. A different
 * name is a newer build, full stop; the same name is not a redeploy, whatever
 * else went wrong. On the dev server the script is plainly `main.js`, so the
 * check can never fire there.
 *
 * Nothing is reloaded on its own: the person may be halfway through a form,
 * and losing it is worse than a banner. The banner explains and offers the
 * reload; this service is only the flag behind it, fed by the error handlers
 * in `core/errors`.
 */
@Service()
export class StaleBuildService {
  private readonly document = inject(DOCUMENT);
  private readonly stale = signal(false);

  /** One check at a time — several failures in a row must not fan out fetches. */
  private pending: Promise<boolean> | undefined;

  /** True once a newer build has been confirmed — sticky until the reload. */
  readonly detected = this.stale.asReadonly();

  /**
   * Inspects an error and, if it is a failed chunk load, verifies against the
   * server whether a newer build is the reason. Resolves to that verdict, so a
   * caller can pick its wording; raises the flag when it is positive.
   */
  async report(error: unknown): Promise<boolean> {
    if (!isStaleChunkError(error)) {
      return false;
    }

    if (this.stale()) {
      return true;
    }

    this.pending ??= this.serverHasNewerBuild().finally(() => {
      this.pending = undefined;
    });

    const stale = await this.pending;

    if (stale) {
      this.stale.set(true);
    }

    return stale;
  }

  /** Fetches the current build. A full reload — the old one is gone anyway. */
  reload(): void {
    this.document.defaultView?.location.reload();
  }

  /**
   * Compares the main script this page runs with the one the server's
   * `index.html` names right now. Anything that stops the comparison — no
   * script tag, a failed fetch, an answer without one — counts as "not stale":
   * the banner must never appear on a guess.
   */
  private async serverHasNewerBuild(): Promise<boolean> {
    const running = mainScriptOf(Array.from(this.document.scripts, (script) => script.src));

    if (!running) {
      return false;
    }

    try {
      const response = await fetch(new URL('index.html', this.document.baseURI), {
        cache: 'no-store',
      });

      if (!response.ok) {
        return false;
      }

      const served = mainScriptOf(scriptSourcesIn(await response.text()));

      return served !== undefined && served !== running;
    } catch {
      return false;
    }
  }
}

/**
 * The messages browsers produce for a dynamic `import()` that failed to fetch
 * or parse. There is no error class for it — it surfaces as a plain
 * `TypeError` — so the text is all there is to go on. The `ChunkLoadError`
 * name is what webpack-built packages throw for the same situation.
 */
const STALE_CHUNK_PATTERNS: readonly RegExp[] = [
  /failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /loading chunk \S+ failed/i, // webpack
];

/** Whether the error is a lazy chunk that could not be loaded. */
export function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(error.message));
}

/** `main.js` on the dev server, `main-<hash>.js` in a build. */
const MAIN_SCRIPT = /^main(-[\w-]+)?\.js$/;

const SCRIPT_SRC = /<script\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/gi;

/**
 * The file name of the application's main script among the given script
 * URLs — path and query stripped, so the same file compares equal whether it
 * came from the DOM (absolute) or from the served HTML (relative).
 */
function mainScriptOf(sources: readonly string[]): string | undefined {
  return sources.map(fileNameOf).find((name) => MAIN_SCRIPT.test(name));
}

function scriptSourcesIn(html: string): string[] {
  return Array.from(html.matchAll(SCRIPT_SRC), (match) => match[1]);
}

function fileNameOf(source: string): string {
  const path = source.split(/[?#]/, 1)[0];

  return path.slice(path.lastIndexOf('/') + 1);
}
